import { useCallback, useEffect, useState } from "react";

/**
 * Iter-276f — Global language preference.
 *
 * Persists the user's selected language in localStorage so the pill
 * choice sticks across navigation and reloads. Broadcasts changes via
 * both a custom event and the standard `storage` event so any surface
 * (Header pill, AdDetailPage auto-translate, etc.) can react in the
 * same tab as well as across tabs.
 *
 * Storage key stays stable to keep buyer state through hot reloads.
 * Only ISO codes are stored — never any translated content.
 */
const KEY = "allsale_lang";
const EVENT = "allsale-lang-changed";

export function getLangPref() {
  try {
    return localStorage.getItem(KEY) || "en";
  } catch {
    return "en";
  }
}

export function setLangPref(code) {
  try {
    if (!code || code === "en") localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, code);
  } catch { /* localStorage disabled */ }
  // Same-tab broadcast — `storage` events don't fire in the tab that
  // wrote them, so a CustomEvent covers the same-tab listener path.
  try {
    window.dispatchEvent(new CustomEvent(EVENT, { detail: { code: code || "en" } }));
  } catch { /* noop */ }
}

export default function useLangPref() {
  const [lang, setLang] = useState(() => getLangPref());

  useEffect(() => {
    const onCustom = (e) => setLang(e?.detail?.code || "en");
    const onStorage = (e) => {
      if (e.key === KEY) setLang(e.newValue || "en");
    };
    window.addEventListener(EVENT, onCustom);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(EVENT, onCustom);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const update = useCallback((code) => {
    setLangPref(code);
    setLang(code || "en");
  }, []);

  return [lang, update];
}
